import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// GENERAL SETTINGS
// ============================================================

export const fetchGeneralSettingsThunk =
  createAsyncThunk(
    "generalSettings/fetch",
    async (_, { rejectWithValue }) => {
      try {
        const res = await api.get(
          "/api/settings/general"
        );

        if (!res.data?.success) {
          return rejectWithValue(
            res.data?.message ||
              "Failed"
          );
        }

        return (
          res.data?.data || {}
        );
      } catch (e) {
        return rejectWithValue(
          e?.response?.data
            ?.message ||
            e.message ||
            "Server error"
        );
      }
    }
  );

// ============================================================
// SAVE GENERAL SETTINGS
// ============================================================

export const saveGeneralSettingsThunk =
  createAsyncThunk(
    "generalSettings/save",

    async (
      payload,
      {
        rejectWithValue,
        dispatch,
      }
    ) => {
      try {
        const fd =
          new FormData();

        // ======================================================
        // BASIC DETAILS
        // ======================================================

        fd.append(
          "title",
          payload.title || ""
        );

        fd.append(
          "slrm_pan_name",
          payload.slrm_pan_name ?? ""
        );

        fd.append(
          "phone",
          payload.phone || ""
        );

        fd.append(
          "address",
          payload.address || ""
        );

        // ======================================================
        // READING SETTINGS
        // ======================================================

        // Water reading charge
        fd.append(
          "reading_charge",
          payload.reading_charge ??
            "0"
        );

        // SLRM reading charge
        fd.append(
          "slrm_reading_charge",
          payload.slrm_reading_charge ??
            "0"
        );

        fd.append(
          "reading_measure",
          payload.reading_measure ??
            "0"
        );

        fd.append(
          "unit_conv",
          payload.unit_conv ??
            "0"
        );

        // ======================================================
        // FINANCIAL YEAR
        // ======================================================

        if (
          payload.fin_year !==
          undefined
        ) {
          fd.append(
            "fin_year",
            payload.fin_year ?? ""
          );
        }

        // ======================================================
        // WATER + SLRM BILLING RULE
        // ======================================================

        fd.append(
          "slrm_water_together",
          payload.slrm_water_together ||
            "no"
        );

        // ======================================================
        // RECEIPT TYPE
        //
        // When SLRM + Water Together = no,
        // receipt must always remain separate.
        // ======================================================

        fd.append(
          "receipt_type",

          payload.slrm_water_together ===
            "yes"
            ? payload.receipt_type ||
                "separate"
            : "separate"
        );

        // ======================================================
        // QR REMOVE FLAGS
        // ======================================================

        fd.append(
          "remove_water_payment_qr",
          payload
            .remove_water_payment_qr ||
            "0"
        );

        fd.append(
          "remove_slrm_payment_qr",
          payload
            .remove_slrm_payment_qr ||
            "0"
        );

        // ======================================================
        // WATER QR FILE
        // ======================================================

        if (
          payload.water_payment_qr_file instanceof
          File
        ) {
          fd.append(
            "water_payment_qr",
            payload
              .water_payment_qr_file
          );
        }

        // ======================================================
        // SLRM QR FILE
        // ======================================================

        if (
          payload.slrm_payment_qr_file instanceof
          File
        ) {
          fd.append(
            "slrm_payment_qr",
            payload
              .slrm_payment_qr_file
          );
        }

        // ======================================================
        // SAVE
        // ======================================================

        const res =
          await api.put(
            "/api/settings/general",
            fd,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        if (
          !res.data?.success
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed"
          );
        }

        // Reload updated settings
        dispatch(
          fetchGeneralSettingsThunk()
        );

        return res.data;
      } catch (e) {
        return rejectWithValue(
          e?.response?.data
            ?.message ||
            e.message ||
            "Server error"
        );
      }
    }
  );

// ============================================================
// MASTER UPDATES
// ============================================================

// ------------------------------------------------------------
// POST /api/settings/update-last-reading
// ------------------------------------------------------------

export const updateLastReadingThunk =
  createAsyncThunk(
    "generalSettings/updateLastReading",

    async (
      _,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.post(
            "/api/settings/update-last-reading"
          );

        if (
          !res.data?.success
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed"
          );
        }

        return res.data;
      } catch (e) {
        return rejectWithValue(
          e?.response?.data
            ?.message ||
            e.message ||
            "Server error"
        );
      }
    }
  );

// ------------------------------------------------------------
// POST /api/settings/update-last-bill-date
// ------------------------------------------------------------

export const updateLastBillDateThunk =
  createAsyncThunk(
    "generalSettings/updateLastBillDate",

    async (
      _,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.post(
            "/api/settings/update-last-bill-date"
          );

        if (
          !res.data?.success
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed"
          );
        }

        return res.data;
      } catch (e) {
        return rejectWithValue(
          e?.response?.data
            ?.message ||
            e.message ||
            "Server error"
        );
      }
    }
  );

// ------------------------------------------------------------
// POST /api/settings/update-op-balance
// ------------------------------------------------------------

export const updateOpBalanceThunk =
  createAsyncThunk(
    "generalSettings/updateOpBalance",

    async (
      _,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await api.post(
            "/api/settings/update-op-balance"
          );

        if (
          !res.data?.success
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed"
          );
        }

        return res.data;
      } catch (e) {
        return rejectWithValue(
          e?.response?.data
            ?.message ||
            e.message ||
            "Server error"
        );
      }
    }
  );

// ============================================================
// CHATBOT HOUSE DETAILS
// ============================================================

// GET /api/settings/chatbot-house-details?house_code=&type=

export const fetchChatbotHouseDetailsThunk =
  createAsyncThunk(
    "generalSettings/fetchChatbotHouseDetails",

    async (
      {
        house_code,
        type,
      },

      {
        rejectWithValue,
      }
    ) => {
      try {
        const res =
          await api.get(
            "/api/settings/chatbot-house-details",
            {
              params: {
                house_code,
                type,
              },
            }
          );

        if (
          !res.data?.success
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed"
          );
        }

        return res.data;
      } catch (e) {
        return rejectWithValue(
          e?.response?.data
            ?.message ||
            e.message ||
            "Server error"
        );
      }
    }
  );

// ============================================================
// SLICE
// ============================================================

const slice =
  createSlice({
    name:
      "generalSettings",

    initialState: {
      data: null,

      loading:
        false,

      saving:
        false,

      error:
        null,

      success:
        null,

      // --------------------------------------------------------
      // CHATBOT
      // --------------------------------------------------------

      chatbotLoading:
        false,

      chatbotData:
        null,

      // --------------------------------------------------------
      // BULK ACTIONS
      // --------------------------------------------------------

      bulkLoading: {
        last_reading:
          false,

        last_bill_date:
          false,

        op_balance:
          false,
      },

      bulkResult:
        null,
    },

    reducers: {
      clearGeneralMsg(
        state
      ) {
        state.error =
          null;

        state.success =
          null;

        state.bulkResult =
          null;

        state.chatbotData =
          null;
      },
    },

    extraReducers:
      (builder) => {
        // ======================================================
        // FETCH GENERAL SETTINGS
        // ======================================================

        builder.addCase(
          fetchGeneralSettingsThunk.pending,
          (state) => {
            state.loading =
              true;

            state.error =
              null;
          }
        );

        builder.addCase(
          fetchGeneralSettingsThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.loading =
              false;

            state.data =
              action.payload;
          }
        );

        builder.addCase(
          fetchGeneralSettingsThunk.rejected,
          (
            state,
            action
          ) => {
            state.loading =
              false;

            state.error =
              action.payload ||
              "Failed";
          }
        );

        // ======================================================
        // SAVE GENERAL SETTINGS
        // ======================================================

        builder.addCase(
          saveGeneralSettingsThunk.pending,
          (state) => {
            state.saving =
              true;

            state.error =
              null;

            state.success =
              null;
          }
        );

        builder.addCase(
          saveGeneralSettingsThunk.fulfilled,
          (state) => {
            state.saving =
              false;

            state.success =
              "Saved successfully";
          }
        );

        builder.addCase(
          saveGeneralSettingsThunk.rejected,
          (
            state,
            action
          ) => {
            state.saving =
              false;

            state.error =
              action.payload ||
              "Failed";
          }
        );

        // ======================================================
        // BULK - LAST READING
        // ======================================================

        builder.addCase(
          updateLastReadingThunk.pending,
          (state) => {
            state.bulkLoading
              .last_reading =
              true;

            state.error =
              null;

            state.success =
              null;

            state.bulkResult =
              null;
          }
        );

        builder.addCase(
          updateLastReadingThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.bulkLoading
              .last_reading =
              false;

            state.success =
              action.payload
                ?.message ||
              "Last Reading Updated";

            state.bulkResult =
              action.payload ||
              null;
          }
        );

        builder.addCase(
          updateLastReadingThunk.rejected,
          (
            state,
            action
          ) => {
            state.bulkLoading
              .last_reading =
              false;

            state.error =
              action.payload ||
              "Failed";
          }
        );

        // ======================================================
        // BULK - LAST BILL DATE
        // ======================================================

        builder.addCase(
          updateLastBillDateThunk.pending,
          (state) => {
            state.bulkLoading
              .last_bill_date =
              true;

            state.error =
              null;

            state.success =
              null;

            state.bulkResult =
              null;
          }
        );

        builder.addCase(
          updateLastBillDateThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.bulkLoading
              .last_bill_date =
              false;

            state.success =
              action.payload
                ?.message ||
              "Last Bill Date Updated";

            state.bulkResult =
              action.payload ||
              null;
          }
        );

        builder.addCase(
          updateLastBillDateThunk.rejected,
          (
            state,
            action
          ) => {
            state.bulkLoading
              .last_bill_date =
              false;

            state.error =
              action.payload ||
              "Failed";
          }
        );

        // ======================================================
        // BULK - OPENING BALANCE
        // ======================================================

        builder.addCase(
          updateOpBalanceThunk.pending,
          (state) => {
            state.bulkLoading
              .op_balance =
              true;

            state.error =
              null;

            state.success =
              null;

            state.bulkResult =
              null;
          }
        );

        builder.addCase(
          updateOpBalanceThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.bulkLoading
              .op_balance =
              false;

            state.success =
              action.payload
                ?.message ||
              "Opening Balance Updated";

            state.bulkResult =
              action.payload ||
              null;
          }
        );

        builder.addCase(
          updateOpBalanceThunk.rejected,
          (
            state,
            action
          ) => {
            state.bulkLoading
              .op_balance =
              false;

            state.error =
              action.payload ||
              "Failed";
          }
        );

        // ======================================================
        // CHATBOT HOUSE DETAILS
        // ======================================================

        builder.addCase(
          fetchChatbotHouseDetailsThunk.pending,
          (state) => {
            state.chatbotLoading =
              true;

            state.error =
              null;

            state.chatbotData =
              null;
          }
        );

        builder.addCase(
          fetchChatbotHouseDetailsThunk.fulfilled,
          (
            state,
            action
          ) => {
            state.chatbotLoading =
              false;

            state.chatbotData =
              action.payload ||
              null;
          }
        );

        builder.addCase(
          fetchChatbotHouseDetailsThunk.rejected,
          (
            state,
            action
          ) => {
            state.chatbotLoading =
              false;

            state.error =
              action.payload ||
              "Failed";
          }
        );
      },
  });

// ============================================================
// EXPORTS
// ============================================================

export const {
  clearGeneralMsg,
} = slice.actions;

export default slice.reducer;